import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material';

export const headerCardSx = (
  theme: Theme,
  open: boolean,
  isMobile: boolean,
  drawerWidth: number,
  bannerHeight: number = 0,
  isRtl: boolean = false,
): SxProps<Theme> => ({
  position: 'fixed',
  top: bannerHeight,
  ...(isRtl
    ? { right: open && !isMobile ? `${drawerWidth}px` : 0, left: 0 }
    : { left: open && !isMobile ? `${drawerWidth}px` : 0, right: 0 }),
  borderRadius: '0',
  py: '16px',
  padding: '0',
  background: theme.palette.background.paper,
  border: 'none',
  width: open && !isMobile ? `calc(100% - ${drawerWidth}px)` : '100%',
  zIndex: (t) => t.zIndex.drawer + 1,
  transition: (t) =>
    t.transitions.create(['left', 'right', 'width'], {
      easing: t.transitions.easing.sharp,
      duration: t.transitions.duration.leavingScreen,
    }),
  [`@media (max-width:${theme.breakpoints.values.md}px)`]: {
    transition: 'none',
  },
});

export const toolbarSx = (isRtl: boolean = false): SxProps<Theme> => ({
  justifyContent: 'space-between',
  ...(isRtl && { flexDirection: 'row-reverse' }),
});

export const leftSectionSx = {
  direction: 'row' as const,
  alignItems: 'center' as const,
};

export const menuButtonSx = (
  open: boolean,
  isMobile: boolean,
): SxProps<Theme> => ({
  marginRight: { xs: 1, sm: 2 },
  ...(open && !isMobile && { display: 'none' }),
});

export const logoImgSx = (open: boolean): SxProps<Theme> => ({
  height: { xs: '34px', sm: '40px' },
  width: 'auto',
  ...(open && { display: 'none' }),
  marginLeft: { xs: '8px', sm: '16px' },
});

export const middleSectionSx = {
  direction: 'row' as const,
  gap: { xs: 1.5, sm: 3, md: 6, lg: 8 }, // responsive spacing
};

export const metricRowSx = {
  direction: 'row' as const,
  alignItems: 'center' as const,
  gap: { xs: 1, sm: 1.5, md: 2 }, // responsive spacing
};

export const metricIconBoxSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#99A8FF1A',
  borderRadius: '4px',
  padding: { xs: '5px', sm: '6px', md: '6px' }, // responsive padding
  gap: { xs: 0.75, sm: 1 }, // icon + value spacing (mobile)
};

export const metricIconSx: SxProps<Theme> = {
  color: '#CCD4FF',
  fontSize: { xs: '22px', sm: '26px', md: '28px', lg: '30px' }, // ✅ responsive icon size
};

export const metricLabelSx: SxProps<Theme> = {
  color: '#CCD4FF',
  fontSize: { md: '14px', lg: '16px' }, // ✅ responsive label size
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
  display: { xs: 'none', md: 'block' }, // hide on mobile
};

const baseValueSx: SxProps<Theme> = {
  fontWeight: 'bold',
  fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '18px' }, // ✅ responsive value size
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
};

export const metricValueSx: SxProps<Theme> = baseValueSx;

export const valueInIconBoxSx: SxProps<Theme> = {
  ...baseValueSx,
  display: { xs: 'inline-flex', md: 'none' }, // mobile only
};

export const valueOutsideIconBoxSx: SxProps<Theme> = {
  ...baseValueSx,
  display: { xs: 'none', md: 'block' }, // desktop only
};

export const metricButtonSx: SxProps<Theme> = {
  fontSize: { md: '10px', lg: '11px' }, // ✅ responsive button text
  p: { md: '2px 14px', lg: '2px 16px' }, // ✅ responsive button padding
  borderRadius: '20px',
  whiteSpace: 'nowrap',
  display: { xs: 'none', md: 'inline-flex' }, // hide on mobile
};

export const rightSectionSx = {
  direction: 'row' as const,
  alignItems: 'center' as const,
  spacing: { xs: 1, sm: 1.5, md: 2 }, // responsive spacing
};

export const newYearButtonSx: SxProps<Theme> = {
  borderRadius: 1.5,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  px: { xs: 1.25, sm: 1.5, md: 2 },
  py: { xs: 0.75, sm: 0.9, md: 1 },
  fontWeight: 700,
  fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.85rem' }, // ✅ responsive
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
  backgroundColor: '#047857',
  '&:hover': {
    backgroundColor: '#065f46',
    boxShadow: '0 6px 14px rgba(0,0,0,0.22)',
  },
};

export const newYearTimerChipSx: SxProps<Theme> = {
  fontSize: { xs: '0.7rem', md: '0.75rem' },
  borderRadius: 999,
  px: 1,
  py: 0.25,
  bgcolor: 'rgba(255,255,255,0.18)',
  border: '1px solid rgba(255,255,255,0.35)',
};

export const notificationsIconSx: SxProps<Theme> = {
  fontSize: { xs: '26px', sm: '28px', md: '32px' }, // ✅ responsive notifications icon
};
